import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send, Loader2, BadgeCheck } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Overline } from "./Reveal";
import { CONTACT } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = ({ compact = false }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please fill in your name, email and message."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! Our team will be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  const info = [
    { icon: Phone, label: "Phone", value: CONTACT.phone, href: CONTACT.phoneHref, tid: "contact-phone-link" },
    { icon: Phone, label: "Mobile", value: CONTACT.phone2, href: CONTACT.phone2Href, tid: "contact-phone2-link" },
    { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, tid: "contact-email-link" },
    { icon: BadgeCheck, label: "Approval", value: CONTACT.ato },
    { icon: MapPin, label: "Location", value: CONTACT.address },
  ];

  return (
    <section id="contact" className="py-24 lg:py-32 bg-navy relative overflow-hidden" data-testid="contact">
      <div className="absolute inset-0 grain" />
      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Overline dark>Contact</Overline>
            <h2 className="mt-5 font-display font-light text-4xl md:text-5xl tracking-tight text-white">Let's get you flying.</h2>
            <p className="mt-5 text-white/60">Visit us at Rand Airport or send a message — our admissions team will guide your first step into aviation.</p>
            <div className="mt-10 space-y-5">
              {info.map((it) => {
                const Body = (
                  <div className="flex gap-4 items-start">
                    <div className="h-11 w-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0"><it.icon size={18} className="text-accent-2" /></div>
                    <div><div className="text-white/40 text-xs uppercase tracking-wide">{it.label}</div><div className="text-white text-sm mt-0.5">{it.value}</div></div>
                  </div>
                );
                return it.href ? <a key={it.label} href={it.href} className="block hover:opacity-80 transition-opacity" data-testid={it.tid}>{Body}</a> : <div key={it.label}>{Body}</div>;
              })}
              <div className="flex gap-4 items-start">
                <div className="h-11 w-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0"><Clock size={18} className="text-accent-2" /></div>
                <div><div className="text-white/40 text-xs uppercase tracking-wide">Operating Hours</div>{CONTACT.hours.map((h) => <div key={h.d} className="text-white/80 text-sm mt-0.5">{h.d} · {h.h}</div>)}</div>
              </div>
            </div>
            <div className="mt-8 rounded-2xl overflow-hidden border border-white/10 h-56">
              <iframe title="Rand Airport map" src={CONTACT.mapEmbed} className="w-full h-full" style={{ filter: "grayscale(0.2)" }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <form onSubmit={submit} className="glass rounded-2xl p-8 md:p-10" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full name" value={form.name} onChange={set("name")} testid="contact-name" />
                <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="contact-email" />
              </div>
              <div className="mt-5"><Field label="Phone (optional)" value={form.phone} onChange={set("phone")} testid="contact-phone" /></div>
              <div className="mt-5">
                <label className="text-white/50 text-xs uppercase tracking-wide">Message</label>
                <textarea rows={5} value={form.message} onChange={set("message")} data-testid="contact-message" className="mt-2 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:border-accent focus:outline-none transition-colors resize-none" placeholder="Tell us about your aviation goals..." />
              </div>
              <button type="submit" disabled={loading} data-testid="contact-submit" className="mt-6 btn-primary px-8 py-4 inline-flex items-center gap-2 disabled:opacity-60">
                {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />} {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <label className="text-white/50 text-xs uppercase tracking-wide">{label}</label>
    <input type={type} value={value} onChange={onChange} data-testid={testid} className="mt-2 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:border-accent focus:outline-none transition-colors" />
  </div>
);
